import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Grid as MuiGrid,
  Chip,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Preview as PreviewIcon } from '@mui/icons-material';
import { groupService, GroupCriteriaOptions } from '../../services/groupService';
import whatsappTemplateService, { WhatsAppTemplate } from '../../services/whatsappTemplateService';
import campaignService from '../../services/campaignService';

const Grid = MuiGrid;

function buildCriteriaPayload(params: {
  propertyIds: number[];
  guestCountries: string[];
  guestStates: string[];
  guestCities: string[];
  statuses: string[];
  startDate: string;
  endDate: string;
  marketSegments: string[];
  bookingTypes: string[];
}): Record<string, unknown> {
  const c: Record<string, unknown> = {};
  if (params.propertyIds.length) c.property_ids = params.propertyIds;
  if (params.guestCountries.length) c.guest_countries = params.guestCountries;
  if (params.guestStates.length) c.guest_states = params.guestStates;
  if (params.guestCities.length) c.guest_cities = params.guestCities;
  if (params.statuses.length) c.status = params.statuses;
  if (params.startDate) c.start_date = params.startDate;
  if (params.endDate) c.end_date = params.endDate;
  if (params.marketSegments.length) c.market_segments = params.marketSegments;
  if (params.bookingTypes.length) c.booking_types = params.bookingTypes;
  return c;
}

const CreateWhatsappCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audienceGroupName, setAudienceGroupName] = useState('');
  const [whatsappTemplateId, setWhatsappTemplateId] = useState<number | null>(null);
  const [waTemplates, setWaTemplates] = useState<WhatsAppTemplate[]>([]);
  const [criteriaOptions, setCriteriaOptions] = useState<GroupCriteriaOptions | null>(null);

  const [propertyIds, setPropertyIds] = useState<number[]>([]);
  const [guestCountries, setGuestCountries] = useState<string[]>([]);
  const [guestStates, setGuestStates] = useState<string[]>([]);
  const [guestCities, setGuestCities] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [marketSegments, setMarketSegments] = useState<string[]>([]);
  const [bookingTypes, setBookingTypes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const criteriaPayload = useMemo(
    () =>
      buildCriteriaPayload({
        propertyIds,
        guestCountries,
        guestStates,
        guestCities,
        statuses,
        startDate,
        endDate,
        marketSegments,
        bookingTypes,
      }),
    [
      propertyIds,
      guestCountries,
      guestStates,
      guestCities,
      statuses,
      startDate,
      endDate,
      marketSegments,
      bookingTypes,
    ]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [tplList, optionsRes] = await Promise.all([
          whatsappTemplateService.getAll({ status: 'APPROVED' }),
          groupService.getGroupCriteriaOptions(),
        ]);
        setWaTemplates(Array.isArray(tplList) ? tplList : []);
        setCriteriaOptions(optionsRes.data);
      } catch (e) {
        console.error(e);
        setError('Failed to load WhatsApp templates or filter options');
      }
    };
    load();
  }, []);

  const handlePreviewAudience = async () => {
    const keys = Object.keys(criteriaPayload);
    if (keys.length === 0) {
      setError('Select at least one filter (e.g. properties, location, or dates)');
      return;
    }
    try {
      setPreviewLoading(true);
      setError(null);
      const res = await groupService.previewAudience({
        ...criteriaPayload,
        campaign_type: 'whatsapp',
      });
      const count = (res as { data?: { count?: number } }).data?.count;
      setPreviewCount(typeof count === 'number' ? count : null);
      setSuccess(`Audience size: ${count ?? '?'} unique phones`);
    } catch (err: unknown) {
      console.error(err);
      setError('Could not preview audience');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Campaign name is required');
      return;
    }
    if (!whatsappTemplateId) {
      setError('Select an approved WhatsApp template (Meta + CRM APPROVED)');
      return;
    }
    const keys = Object.keys(criteriaPayload);
    if (keys.length === 0) {
      setError('Select at least one audience filter');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const created = await campaignService.createCampaign({
        name: name.trim(),
        description: description.trim(),
        campaign_type: 'whatsapp',
        whatsapp_template_id: whatsappTemplateId,
        audience_criteria: criteriaPayload,
        audience_group_name: audienceGroupName.trim() || undefined,
      });
      setSuccess('WhatsApp campaign created');
      setTimeout(() => navigate(`/campaigns/${created.id}`), 800);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; [k: string]: unknown } } };
      const msg =
        ax.response?.data &&
        typeof ax.response.data === 'object' &&
        'message' in ax.response.data
          ? String(ax.response.data.message)
          : 'Failed to create campaign';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const propsList = criteriaOptions?.properties || [];

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" onClick={() => navigate('/campaigns')} sx={{ cursor: 'pointer' }}>
          Campaigns
        </Link>
        <Typography color="text.primary">Create WhatsApp campaign</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        Create WhatsApp campaign
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Uses Meta WhatsApp Cloud API template messages. Configure{' '}
        <code>meta_template_name</code>, <code>language_code</code>, and{' '}
        <code>body_parameter_sources</code> (same variable keys as email) on the template. Server
        needs <code>WHATSAPP_CLOUD_ACCESS_TOKEN</code> and <code>WHATSAPP_PHONE_NUMBER_ID</code>.
        Reservations linked to an agent are never included.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid style={{ gridColumn: 'span 12' }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Campaign
              </Typography>
              <Stack spacing={2}>
                <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
                <TextField
                  label="Audience group label (optional)"
                  value={audienceGroupName}
                  onChange={(e) => setAudienceGroupName(e.target.value)}
                  helperText="Shown as the auto-generated group name suffix"
                  fullWidth
                />
                <FormControl fullWidth required>
                  <InputLabel>WhatsApp template (APPROVED)</InputLabel>
                  <Select
                    value={whatsappTemplateId ?? ''}
                    label="WhatsApp template (APPROVED)"
                    onChange={(e) => setWhatsappTemplateId(Number(e.target.value))}
                  >
                    {waTemplates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                        {t.meta_template_name && t.meta_template_name !== t.name
                          ? ` — Meta: ${t.meta_template_name}`
                          : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {waTemplates.length === 0 && (
                  <Alert severity="warning">
                    No approved templates. Add one under WhatsApp Templates and set status to APPROVED
                    after Meta approves the template.
                  </Alert>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Audience filters (multi-select)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Combine property and guest location filters as needed. At least one filter is required.
              </Typography>

              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  options={propsList}
                  getOptionLabel={(o) => `${o.name} (${o.property_id})`}
                  value={propsList.filter((p) => propertyIds.includes(p.id))}
                  onChange={(_, v) => setPropertyIds(v.map((p) => p.id))}
                  renderInput={(params) => <TextField {...params} label="Properties" placeholder="Select properties" />}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                    ))
                  }
                />

                <Autocomplete
                  multiple
                  freeSolo
                  options={criteriaOptions?.countries || []}
                  value={guestCountries}
                  onChange={(_, v) => setGuestCountries(v.map(String))}
                  renderInput={(params) => <TextField {...params} label="Guest countries" />}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={criteriaOptions?.states || []}
                  value={guestStates}
                  onChange={(_, v) => setGuestStates(v.map(String))}
                  renderInput={(params) => <TextField {...params} label="Guest states" />}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={criteriaOptions?.cities || []}
                  value={guestCities}
                  onChange={(_, v) => setGuestCities(v.map(String))}
                  renderInput={(params) => <TextField {...params} label="Guest cities" />}
                />

                <Autocomplete
                  multiple
                  options={criteriaOptions?.statuses || []}
                  value={statuses}
                  onChange={(_, v) => setStatuses(v)}
                  renderInput={(params) => <TextField {...params} label="Reservation status" />}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Arrival from (start_date)"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Departure by (end_date)"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>

                <Autocomplete
                  multiple
                  options={criteriaOptions?.market_segments || []}
                  value={marketSegments}
                  onChange={(_, v) => setMarketSegments(v)}
                  renderInput={(params) => <TextField {...params} label="Market segments" />}
                />
                <Autocomplete
                  multiple
                  options={criteriaOptions?.booking_types || []}
                  value={bookingTypes}
                  onChange={(_, v) => setBookingTypes(v)}
                  renderInput={(params) => <TextField {...params} label="Booking types" />}
                />

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={previewLoading ? <CircularProgress size={18} /> : <PreviewIcon />}
                    onClick={handlePreviewAudience}
                    disabled={previewLoading}
                  >
                    Preview audience count
                  </Button>
                  {previewCount !== null && (
                    <Chip color="primary" label={`${previewCount} unique phones`} />
                  )}
                </Stack>
              </Stack>
            </Paper>

            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Creating…' : 'Create campaign'}
              </Button>
              <Button type="button" variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate('/campaigns')}>
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success">{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateWhatsappCampaign;
